'use client'

// Shared PathCoins wallet card — the dark branded summary used on the Home
// dashboard and the dedicated /me/path-coins page (issue: keep both in sync).

import Link from 'next/link'

const FONT_UI = 'font-[family-name:var(--font-rounded)]'
const FONT_HEADING = 'font-[family-name:var(--font-sora)]'
const FONT_BODY = 'font-[family-name:var(--font-nunito)]'

// Monthly PathCoins goal shown on the wallet progress bar.
export const MONTHLY_COIN_TARGET = 250

export interface PathCoinsBalance {
  balance_total: number
  balance_redeemed: number
  balance_available: number
}

export function PathCoinsWalletCard({
  balance,
  monthEarned,
  showHistoryLink = false,
  balanceTestId,
}: {
  balance: PathCoinsBalance | null
  monthEarned: number
  showHistoryLink?: boolean
  balanceTestId?: string
}) {
  const progressPct = Math.min(100, Math.round((monthEarned / MONTHLY_COIN_TARGET) * 100))

  return (
    <div
      data-testid="pathcoins-wallet-card"
      className="bg-[#2C2D2B] rounded-[20px] p-6 text-[#F4EAD8] flex flex-col gap-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(44,45,43,.32)]"
    >
      <div className="flex items-center gap-3.5">
        <div
          className={`${FONT_HEADING} w-[46px] h-[46px] rounded-full flex-shrink-0 flex items-center justify-center font-extrabold text-[17px] text-[#7a5414] shadow-[inset_0_0_0_2px_rgba(255,255,255,.35),0_2px_8px_rgba(0,0,0,.35)]`}
          style={{ background: 'radial-gradient(circle at 32% 28%, #F7CF86, #F0B853 60%, #D99E37)' }}
        >
          P
        </div>
        <div>
          <div className={`${FONT_HEADING} flex items-baseline gap-2 font-extrabold leading-none text-[#F0B853]`}>
            <span className="text-[40px] tracking-tight" data-testid={balanceTestId}>
              {balance?.balance_available ?? 0}
            </span>
            <span className="text-[15px] text-[#F4EAD8]/60">PC</span>
          </div>
          <div className={`${FONT_UI} font-bold text-xs tracking-[.03em] text-[#F4EAD8]/60 mt-1`}>PATH Coins · available balance</div>
        </div>
      </div>
      <div>
        <div className={`${FONT_BODY} flex justify-between items-baseline text-xs text-[#F4EAD8]/60 mb-1.5`}>
          <span>This month&apos;s contribution</span>
          <span><b className="text-[#F4EAD8] font-bold">{monthEarned}</b> / {MONTHLY_COIN_TARGET}</span>
        </div>
        <div className="h-[9px] rounded-full bg-[#F4EAD8]/15 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#75A096] to-[#F0B853]" style={{ width: `${progressPct}%` }} />
        </div>
      </div>
      <div className="flex gap-6">
        <div>
          <div className={`${FONT_BODY} flex items-center gap-1.5 font-bold text-[10.5px] tracking-[.06em] uppercase text-[#F4EAD8]/55`}>
            <span className="w-2 h-2 rounded-full bg-[#75A096]" /> Earned
          </div>
          <div className={`${FONT_HEADING} font-extrabold text-lg text-[#F4EAD8] mt-0.5`}>{balance?.balance_total ?? 0} PC</div>
        </div>
        <div>
          <div className={`${FONT_BODY} flex items-center gap-1.5 font-bold text-[10.5px] tracking-[.06em] uppercase text-[#F4EAD8]/55`}>
            <span className="w-2 h-2 rounded-full bg-[#DE866D]" /> Redeemed
          </div>
          <div className={`${FONT_HEADING} font-extrabold text-lg text-[#F4EAD8] mt-0.5`}>{balance?.balance_redeemed ?? 0} PC</div>
        </div>
      </div>
      {showHistoryLink && (
        <Link href="/me/path-coins" className={`${FONT_UI} self-start font-bold text-[13px] text-[#F0B853] hover:underline`}>
          View history →
        </Link>
      )}
    </div>
  )
}
